import { LifeBuoy, MessageCircleQuestion, BookOpen, ArrowUpRight } from "lucide-react";
import Link from "@/components/ui/RouterLink";
import { PageHero } from "@/components/ui/PageHero";
import { SectionEyebrow } from "@/components/ui/SectionEyebrow";
import { Badge } from "@/components/ui/Badge";
import { pageSeo } from "@/config/seo";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { whmcs } from "@/config/whmcs";
import { analytics } from "@/lib/analytics";
import { supportResources } from "@/data/support-resources";

const seo = pageSeo(
  "Support — Get Help From The Planet",
  "Access your account and invoices, ask a product question before purchasing, or find technical resources."
);

export function SupportPage() {
  useDocumentMeta(seo.title, seo.description);

  return (
    <>
      <PageHero
        eyebrow="Support"
        title="Get the Help You Need"
        description="Whether you're an existing customer or still deciding, here's where to go."
      />

      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="flex flex-col rounded-card border border-border bg-navy-card p-7">
            <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-border bg-navy-secondary text-cyan">
              <LifeBuoy className="h-5 w-5" strokeWidth={1.6} />
            </div>
            <h3 className="mt-5 font-display text-lg font-semibold text-text-primary">
              Existing Customer
            </h3>
            <p className="mt-2.5 flex-1 text-sm leading-relaxed text-text-secondary">
              Access services, invoices, payments and tickets through the client area.
            </p>
            <a
              href={whmcs.loginUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => analytics.track("client_login_click")}
              className="focus-ring mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-cyan hover:underline"
            >
              Open Client Area <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
          </div>

          <div className="flex flex-col rounded-card border border-border bg-navy-card p-7">
            <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-border bg-navy-secondary text-cyan">
              <MessageCircleQuestion className="h-5 w-5" strokeWidth={1.6} />
            </div>
            <h3 className="mt-5 font-display text-lg font-semibold text-text-primary">
              Product Questions
            </h3>
            <p className="mt-2.5 flex-1 text-sm leading-relaxed text-text-secondary">
              Speak with the team before purchasing.
            </p>
            <Link
              href="/contact"
              className="focus-ring mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-cyan hover:underline"
            >
              Contact Sales <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>

          <div className="flex flex-col rounded-card border border-border bg-navy-card p-7">
            <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-border bg-navy-secondary text-cyan">
              <BookOpen className="h-5 w-5" strokeWidth={1.6} />
            </div>
            <h3 className="mt-5 font-display text-lg font-semibold text-text-primary">
              Technical Resources
            </h3>
            <p className="mt-2.5 text-sm leading-relaxed text-text-secondary">
              Documentation and status pages, as they become available.
            </p>
            <ul className="mt-5 space-y-2.5 border-t border-border pt-5">
              {supportResources.map((r) => (
                <li key={r.label} className="flex items-center justify-between text-sm">
                  <span className="text-text-secondary">{r.label}</span>
                  {r.available && r.href ? (
                    <a href={r.href} className="focus-ring text-cyan hover:underline">
                      Open
                    </a>
                  ) : (
                    <Badge tone="warning">Coming soon</Badge>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="border-t border-border bg-navy-secondary/40 py-16 text-center">
        <SectionEyebrow>Still Need Help?</SectionEyebrow>
        <h2 className="mx-auto mt-4 max-w-md font-display text-2xl font-semibold text-text-primary">
          Our team is ready to help you with billing or support requests.
        </h2>
        <div className="mt-7">
          <a
            href={whmcs.supportUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="focus-ring inline-flex items-center justify-center gap-2 rounded-full bg-gradient-brand px-6 py-3.5 text-sm font-medium text-white transition hover:brightness-110"
          >
            Open a Support Ticket
          </a>
        </div>
      </section>
    </>
  );
}
